// Piezas compartidas de la vista Semáforo: semáforo por ticket, pill de
// estado, cifras, barra contra la meta, marcador «por confirmar» y el selector
// de persona local (solo desarrollo).
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Bell, LayoutGrid, Megaphone, Ticket as TicketIcon, Users } from "lucide-react";
import {
  Boton,
  Pill,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  Semaforo,
  Vacio,
  cn,
} from "@iavmx/ui";
import { fijarPersonaLocal, usePersona, useRefrescar, type Ticket } from "./datos";
import { estadoLabel, relativo } from "./formato";

export function SemaforoTicket({ ticket, ahora }: { ticket: Ticket; ahora?: Date }) {
  const a = ahora ?? new Date();
  if (ticket.vencido)
    return <Semaforo color="rojo">Vencido {relativo(ticket.venceEn, a)}</Semaforo>;
  if (ticket.semaforo === "rojo") return <Semaforo color="rojo">Vence {relativo(ticket.venceEn, a)}</Semaforo>;
  if (ticket.semaforo === "ambar") return <Semaforo color="ambar">Vence {relativo(ticket.venceEn, a)}</Semaforo>;
  if (ticket.semaforo === "verde") return <Semaforo color="verde">A tiempo · vence {relativo(ticket.venceEn, a)}</Semaforo>;
  if (ticket.estado === "entregada" || ticket.estado === "cerrada")
    return <Semaforo color="gris">{estadoLabel(ticket.estado)}</Semaforo>;
  return <Semaforo color="gris">Sin fecha límite</Semaforo>;
}

const TONO_ESTADO: Record<string, "neutro" | "tinta" | "azul" | "verde" | "ambar" | "rojo"> = {
  nueva: "azul",
  incompleta: "ambar",
  asignada: "neutro",
  en_proceso: "neutro",
  en_revision: "azul",
  entregada: "verde",
  cerrada: "tinta",
  cancelada: "neutro",
};

export function PillEstado({ estado }: { estado: string }) {
  return <Pill tono={TONO_ESTADO[estado] ?? "neutro"}>{estadoLabel(estado)}</Pill>;
}

/** Supuesto tomado del PID sin intake: se marca en la interfaz hasta confirmarlo. */
export function PorConfirmar({ que }: { que?: string }) {
  return (
    <span
      className="font-ui text-[11px] font-semibold uppercase tracking-wide text-tinta-tenue underline decoration-dotted underline-offset-2"
      title={que ? `${que} · por confirmar con Estrategia Digital` : "Por confirmar con Estrategia Digital"}
    >
      por confirmar
    </span>
  );
}

export function Cifra({
  etiqueta,
  valor,
  sufijo,
  nota,
  tono,
}: {
  etiqueta: string;
  valor: React.ReactNode;
  sufijo?: string;
  nota?: React.ReactNode;
  tono?: "rojo" | "ambar" | "verde";
}) {
  return (
    <div className="flex flex-col gap-1.5 py-4">
      <span className="font-ui text-[11px] font-semibold uppercase tracking-wide text-tinta-tenue">{etiqueta}</span>
      <span
        className={cn(
          "font-titulo text-4xl font-bold leading-none tabular-nums lg:text-5xl",
          tono === "rojo" && "text-rojo",
          tono === "ambar" && "text-ambar",
          tono === "verde" && "text-verde",
        )}
      >
        {valor}
        {sufijo && <span className="text-2xl">{sufijo}</span>}
      </span>
      {nota && <span className="font-ui text-sm text-tinta-tenue">{nota}</span>}
    </div>
  );
}

/** Barra de cumplimiento con la línea de la meta. */
export function BarraMeta({ pct, meta }: { pct: number | null; meta: number }) {
  return (
    <div className="relative h-5 w-full py-1" aria-label={pct === null ? "Sin entregas" : `${pct} % contra meta ${meta} %`}>
      <div className="h-3 w-full bg-fondo-alzado">
        <div className="h-3 bg-azul" style={{ width: `${Math.min(100, pct ?? 0)}%` }} />
      </div>
      <div className="absolute top-0 h-5 w-px bg-tinta" style={{ left: `${meta}%` }} aria-hidden="true" />
    </div>
  );
}

export function Segmento<T extends string>({
  opciones,
  valor,
  onChange,
  etiqueta,
}: {
  opciones: { valor: T; texto: string }[];
  valor: T;
  onChange: (v: T) => void;
  etiqueta: string;
}) {
  return (
    <div className="inline-flex border border-tinta" role="group" aria-label={etiqueta}>
      {opciones.map((o) => (
        <button
          key={o.valor}
          type="button"
          aria-pressed={o.valor === valor}
          onClick={() => onChange(o.valor)}
          className={cn(
            "h-10 px-3.5 font-ui text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            o.valor === valor ? "bg-tinta text-tinta-inversa" : "text-tinta-2 hover:bg-fondo-suave",
          )}
        >
          {o.texto}
        </button>
      ))}
    </div>
  );
}

export function Migas({ partes }: { partes: { texto: string; to?: "/" | "/tickets" | "/tablero" | "/area" }[] }) {
  return (
    <nav aria-label="Ruta" className="mb-2 font-ui text-sm text-tinta-tenue">
      {partes.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden="true"> / </span>}
          {p.to ? (
            <Link to={p.to} className="-my-3 inline-flex min-h-11 items-center text-azul hover:underline">
              {p.texto}
            </Link>
          ) : (
            <span>{p.texto}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

export function Seccion({ titulo, extra, children, className }: { titulo: string; extra?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn("flex min-w-0 flex-col gap-3", className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-titulo text-lg font-bold">{titulo}</h2>
        {extra && <span className="font-ui text-sm text-tinta-tenue">{extra}</span>}
      </div>
      {children}
    </section>
  );
}

export function FilaDato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-linea py-2 font-ui text-sm last:border-0">
      <span className="text-tinta-tenue">{etiqueta}</span>
      <span className="text-right text-tinta">{children}</span>
    </div>
  );
}

export function ErrorCarga({ error, titulo }: { error: Error; titulo?: string }) {
  return <Vacio titulo={titulo ?? "No se pudo cargar"} texto={error.message} />;
}

/** Sesión del Semáforo para la cabecera: persona real o local, y el selector de desarrollo. */
export function useSesionSemaforo() {
  const yo = usePersona();
  const refrescar = useRefrescar();
  const persona = yo.data?.persona ?? null;
  const modoLocal = yo.data?.modoLocal ?? false;
  const selector =
    modoLocal && yo.data?.personasLocales ? (
      <Select
        value={persona?.correo ?? ""}
        onValueChange={(correo) => {
          fijarPersonaLocal(correo);
          void refrescar();
        }}
      >
        {/* Solo desarrollo; en celular no cabe en la cabecera junto al menú, el tema y el avatar, y ensancharía la página. */}
        <SelectDisparador className="hidden h-8 w-auto max-w-[22rem] gap-2 border-dashed text-xs sm:flex" aria-label="Actuar como (solo desarrollo)">
          <span className="shrink-0 font-semibold uppercase tracking-wide text-tinta-tenue">Actúo como</span>
          <SelectValor placeholder="Persona local" />
        </SelectDisparador>
        <SelectContenido>
          {yo.data.personasLocales.map((p) => (
            <SelectOpcion key={p.correo} value={p.correo}>
              {p.nombre} · {p.area ?? "sin área"}
            </SelectOpcion>
          ))}
        </SelectContenido>
      </Select>
    ) : null;
  return { yo: yo.data ?? null, persona, modoLocal, selector, cargando: yo.isLoading };
}

export function BotonNuevoTicket({ className }: { className?: string }) {
  return (
    <Boton variante="acento" asChild className={className}>
      <Link to="/tickets/nuevo">Nuevo ticket</Link>
    </Boton>
  );
}

/** Entradas del menú de la vista Semáforo (Raiz.tsx las usa con una línea). */
export const ENTRADAS_NAV_SEMAFORO: { etiqueta: string; to: "/" | "/campanas" | "/tickets" | "/area" | "/avisos"; icono: React.ReactNode }[] = [
  { etiqueta: "Tablero", to: "/", icono: <LayoutGrid /> },
  { etiqueta: "Tickets", to: "/tickets", icono: <TicketIcon /> },
  { etiqueta: "Mi área", to: "/area", icono: <Users /> },
  { etiqueta: "Avisos", to: "/avisos", icono: <Bell /> },
  { etiqueta: "Campañas", to: "/campanas", icono: <Megaphone /> },
];

/**
 * Lo que la cabecera necesita del Semáforo: la persona local cuando no hay
 * sesión real y el selector de desarrollo en lugar de «Salir».
 */
export function useCabeceraSemaforo(): {
  persona: { nombre: string; correo: string } | null;
  acciones: React.ReactNode | null;
} {
  const s = useSesionSemaforo();
  return {
    persona: s.persona ? { nombre: s.persona.nombre, correo: s.persona.correo } : null,
    acciones: s.modoLocal ? s.selector : null,
  };
}
